/**
 * Navigation Reference — Global navigation bridge for non-React contexts.
 *
 * Problem: OneSignal notification click handlers run outside the React tree,
 * so they can't use useNavigate(). On native, the app uses MemoryRouter which
 * doesn't react to window.location changes.
 *
 * Solution: Store a reference to the React Router navigate function that can
 * be called from anywhere (OneSignal handlers, native plugin callbacks, etc.).
 */

type NavigateFn = (to: string, options?: { replace?: boolean }) => void;

let _navigate: NavigateFn | null = null;

/**
 * Set the global navigate function. Called once from DeepLinkHandler
 * (which lives inside the Router context and has access to useNavigate).
 */
export function setGlobalNavigate(fn: NavigateFn): void {
  _navigate = fn;
}

/**
 * Navigate to a route from outside the React tree.
 * Used by OneSignal notification click handlers, native deep link callbacks, etc.
 *
 * Falls back to window.location for web (BrowserRouter) if navigate ref not set.
 */
export function globalNavigate(to: string, options?: { replace?: boolean }): void {
  if (_navigate) {
    _navigate(to, options);
  } else {
    // Fallback for web — should rarely happen
    window.location.href = to;
  }
}
